import React, { useState, useEffect, useCallback } from "react";
import TablePageLayout from "../../../components/TablePageLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../shadcn/ui/table";
import { Badge } from "../../../shadcn/ui/badge";
import { Button } from "../../../shadcn/ui/button";
import { organizationApiService } from "../../../services/apiV2Service";
import { showApiMessage } from "../../../utils/messageHandler";
import logger from "../../../utils/logger";
import { FaEllipsisH } from "react-icons/fa";
import EditOrganizationModal from "../../host-experience/host-clients/components/modals/EditOrganizationModal";
import { useHostOrganizationEdit } from "../hooks/useHostOrganizationEdit";

const CompanyApprovals = () => {
  const [pendingHosts, setPendingHosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPageSize, setTotalPageSize] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(true);

  // Edit functionality using shared hook
  const {
    showEditModal,
    selectedOrganization,
    editFormData,
    editLoading,
    editError,
    fetchingData,
    commissionPercentage,
    setCommissionPercentage,
    isCustomRevenueShare,
    defaultCommission,
    handleEditShow,
    handleEditClose,
    handleInputChange,
    handleSubmit,
    handleDeleteRevenueShare,
  } = useHostOrganizationEdit();

  const loadOrganizationsByStatus = useCallback(
    async (page = currentPage, search = searchValue) => {
      setLoading(true);
      try {
        // Get INACTIVE organizations (pending approval) for both HOST_PROPERTY_DISTRIBUTION and HOST_PROPERTY_MANAGEMENT types
        const distributionParams = {
          typeId: 2, // HOST_PROPERTY_DISTRIBUTION
          status: "INACTIVE",
          page: page,
          limit: 10,
          ...(search && { q: search }), // Add q param if search exists
        };

        const managementParams = {
          typeId: 3, // HOST_PROPERTY_MANAGEMENT
          status: "INACTIVE",
          page: page,
          limit: 10,
          ...(search && { q: search }), // Add q param if search exists
        };

        const [distributionResponse, managementResponse] = await Promise.all([
          organizationApiService.getOrganizations(distributionParams),
          organizationApiService.getOrganizations(managementParams),
        ]);

        const distributionOrgs = distributionResponse.data?.data || [];
        const managementOrgs = managementResponse.data?.data || [];
        const allOrganizations = [...distributionOrgs, ...managementOrgs];

        const pagination = distributionResponse.data?.pagination || {};

        setTotalPageSize(pagination.totalCount || allOrganizations.length);
        setItemsPerPage(pagination.limit || 10);
        setPendingHosts(allOrganizations);
      } catch (err) {
        logger.error("Error loading inactive organizations:", err);
        showApiMessage(err, "fetch inactive organizations");
      } finally {
        setLoading(false);
      }
    },
    [currentPage, searchValue]
  );

  useEffect(() => {
    loadOrganizationsByStatus();
  }, []);

  // Auto-search after 3 characters
  useEffect(() => {
    if (searchValue.length >= 3 || searchValue.length === 0) {
      setCurrentPage(1);
      loadOrganizationsByStatus(1, searchValue);
    }
  }, [searchValue]);

  const performSearch = async e => {
    if (e && e.preventDefault) e.preventDefault();
    // Reset to first page when searching
    setCurrentPage(1);
    setLoading(true);

    try {
      // For now, we'll use the same loadOrganizationsByStatus function
      // Later this can be enhanced to include search functionality
      await loadOrganizationsByStatus(1, searchValue);
    } catch (err) {
      logger.error("Error searching hosts:", err);
      showApiMessage(err, "search hosts");
    } finally {
      setLoading(false);
    }
  };

  const changePage = selectedObj => {
    // Extract the page number from the object passed from the Pagination component
    const pageNumber = Number(selectedObj.selected) || 1;

    // Save as current page (1-based for UI)
    setCurrentPage(pageNumber);

    // Reload data for the new page
    loadOrganizationsByStatus(pageNumber, searchValue);
  };

  const handleApprove = async (hostId) => {
    try {
      await organizationApiService.updateApprovedStatus(hostId, { approved: true });
      showApiMessage({ message: "Organization approved successfully" }, "success");
      loadOrganizationsByStatus();
    } catch (err) {
      logger.error("Error approving organization:", err);
      showApiMessage(err, "approve organization");
    }
  };

  return (
    <>
      <TablePageLayout
        title="Inactive Hosts"
        subtitle="Manage and view hosts pending approval"
        breadcrumbs={[{ label: "Home", path: "/host" }, { label: "Inactive Hosts" }]}
        showSearch={true}
        searchValue={searchValue}
        onSearchChange={e => setSearchValue(e.target.value)}
        onSearchSubmit={performSearch}
        searchPlaceholder="Search by company name, email, phone, or location..."
        loading={loading}
        emptyMessage="No inactive hosts found"
        showPagination={Array.isArray(pendingHosts) && pendingHosts.length > 0 && totalPageSize > 0}
        currentPage={currentPage}
        totalCount={totalPageSize}
        pageSize={itemsPerPage}
        onPageChange={changePage}
      >
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-12 font-semibold">#</TableHead>
                <TableHead className="font-semibold">Company</TableHead>
                <TableHead className="font-semibold">Contact</TableHead>
                <TableHead className="font-semibold">Organization Type</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Date Submitted</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingHosts.map((host, index) => (
                <TableRow key={host.id} className="hover:bg-muted/50">
                  <TableCell className="text-gray-500 font-medium">
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{host.name}</span>
                      <span className="text-sm text-gray-600">{host.address || "N/A"}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="text-sm">{host.contactEmail || "N/A"}</span>
                      <span className="text-sm text-gray-600">{host.contactMsisdn || "N/A"}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {host.typeId === 2
                        ? "Host Property Distribution"
                        : host.typeId === 3
                          ? "Host Property Management"
                          : "Unknown Type"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 items-start">
                      <Badge variant="secondary" className="bg-orange-100 text-orange-800 hover:bg-orange-100">
                        {host.status || "INACTIVE"}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={
                          host.approved
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-gray-50 text-gray-600 border-gray-200"
                        }
                      >
                        {host.approved ? "Approved" : "Pending Approval"}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div className="font-medium">{new Date(host.createdAt).toLocaleDateString()}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(host.createdAt).toLocaleTimeString()}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2 items-center justify-end">
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => handleApprove(host.id)}
                      >
                        Approve
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEditShow(host)} title="Edit Host">
                        <FaEllipsisH className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TablePageLayout>

      {/* Edit Host Dialog */}
      <EditOrganizationModal
        isOpen={showEditModal}
        onClose={handleEditClose}
        selectedOrganization={selectedOrganization}
        editOrganizationForm={editFormData}
        editOrganizationLoading={editLoading}
        editOrganizationError={editError}
        fetchingOrganizationData={fetchingData}
        onInputChange={handleInputChange}
        onSubmit={async () => {
          const result = await handleSubmit();
          if (result?.success) {
            loadOrganizationsByStatus();
          }
        }}
        isHostMode={true}
        commissionPercentage={commissionPercentage}
        onCommissionChange={setCommissionPercentage}
        defaultCommission={defaultCommission}
        isCustomRevenueShare={isCustomRevenueShare}
        onDeleteRevenueShare={handleDeleteRevenueShare}
      />
    </>
  );
};

export default CompanyApprovals;
